import { useCallback, useMemo } from "react";
import { useAuthChecker } from "../Context";
import { useUserFeaturesQuery } from "../Services";
import { useAccessPolicyOverridesQuery } from "../Services/access_policy_service";
import { MANAGEABLE_ROLES } from "../Constant/rbac";
import { getHiddenFeatureCodes } from "../Constant";
import { useSystemConfig } from "./useSystemConfig";
import type {
  PolicyPath,
  AccessCheckResult,
  AccessibleFeature,
  RBAProfile,
  FeatureData,
} from "../Types";
import type { UserRoles } from "../Constant";
import type { PolicyOverrideMap } from "../Api/access_policy_api";
import { getRoleName, Role } from "../Enums";

const toLower = (s: string): string => s.toLowerCase();

const isAdminRole = (role: string): boolean =>
  toLower(role) === getRoleName(Role.ADMIN);

const resolvePolicyRoles = (
  path: string,
  defaults?: PolicyOverrideMap,
  overrides?: PolicyOverrideMap,
): string[] | null => {
  if (overrides && overrides[path] !== undefined) {
    return overrides[path];
  }
  if (defaults && defaults[path] !== undefined) {
    return defaults[path];
  }
  return null;
};

const policyIncludesRole = (allowedRoles: string[], role: string): boolean => {
  const lowerRole = toLower(role);
  return allowedRoles.some((r) => toLower(r) === lowerRole);
};

export const useRBA = () => {
  const {
    user,
    isAuthenticated,
    isLoading: authLoading,
    isAdmin,
  } = useAuthChecker();

  const featuresQuery = useUserFeaturesQuery();
  const overridesQuery = useAccessPolicyOverridesQuery();
  const { config } = useSystemConfig();

  const queryData = overridesQuery.data as
    { defaults: PolicyOverrideMap; overrides: PolicyOverrideMap } | undefined;
  const defaults = queryData?.defaults;
  const overrides = queryData?.overrides;

  const isLoading =
    authLoading || featuresQuery.isLoading || overridesQuery.isLoading;
  const isReady = !isLoading;
  const role = (user?.userRole ?? "none") as UserRoles;
  const roleLower = toLower(role as string);
  const isSuperAdmin = isAdmin;

  const isAllowed = useCallback(
    (path: PolicyPath | string): boolean => {
      if (!isAuthenticated) return false;

      if (isAdminRole(role as string)) return true;

      const allowedRoles = resolvePolicyRoles(path, defaults, overrides);
      if (allowedRoles === null) return true;

      return policyIncludesRole(allowedRoles, role as string);
    },
    [isAuthenticated, role, defaults, overrides],
  );

  const isDenied = useCallback(
    (path: PolicyPath | string): boolean => !isAllowed(path),
    [isAllowed],
  );

  const allowedRoles = useCallback(
    (path: PolicyPath | string): string[] | null => {
      return resolvePolicyRoles(path, defaults, overrides);
    },
    [defaults, overrides],
  );

  const checkAccess = useCallback(
    (path: PolicyPath | string): AccessCheckResult => {
      if (!isAuthenticated) {
        return {
          allowed: false,
          level: "denied",
          reason: "not_authenticated",
          policyPath: path,
        };
      }

      if (isAdminRole(role as string)) {
        return {
          allowed: true,
          level: "granted",
          reason: "super_admin",
          policyPath: path,
        };
      }

      const allowedRolesList = resolvePolicyRoles(path, defaults, overrides);
      if (allowedRolesList === null) {
        return {
          allowed: true,
          level: "granted",
          reason: "no_policy_restriction",
          policyPath: path,
        };
      }

      if (policyIncludesRole(allowedRolesList, role as string)) {
        return {
          allowed: true,
          level: "granted",
          reason: "role_allowed",
          policyPath: path,
        };
      }

      return {
        allowed: false,
        level: "denied",
        reason: "role_not_allowed",
        policyPath: path,
      };
    },
    [isAuthenticated, role, defaults, overrides],
  );

  const hasRole = useCallback(
    (roles: UserRoles | UserRoles[]): boolean => {
      if (Array.isArray(roles)) {
        return roles.some((r) => toLower(r as string) === roleLower);
      }
      return toLower(roles as string) === roleLower;
    },
    [roleLower],
  );

  const hasAnyRole = useCallback(
    (roles: UserRoles[]): boolean =>
      roles.some((r) => toLower(r as string) === roleLower),
    [roleLower],
  );

  const hiddenFeatureCodes = useMemo(
    () => getHiddenFeatureCodes(config ?? undefined),
    [config],
  );

  const isFeatureEnabled = useCallback(
    (code: string): boolean => {
      if (hiddenFeatureCodes.includes(code)) return false;
      if (!featuresQuery.data) return true;
      const features = featuresQuery.data as FeatureData[];
      for (const feature of features) {
        if (feature.feature_code === code) return true;
        if (feature.sub_features) {
          for (const sub of feature.sub_features) {
            if (sub.code === code) return sub.is_enabled === 1;
          }
        }
      }
      return true;
    },
    [featuresQuery.data, hiddenFeatureCodes],
  );

  const isFeatureDisabled = useCallback(
    (code: string): boolean => !isFeatureEnabled(code),
    [isFeatureEnabled],
  );

  const accessibleRoles = useMemo((): string[] => {
    if (isSuperAdmin) return [...Object.keys(MANAGEABLE_ROLES)];
    const roles = MANAGEABLE_ROLES[role];
    return roles ? [...roles] : [];
  }, [isSuperAdmin, role]);

  const accessibleFeatures = useMemo((): AccessibleFeature[] => {
    if (!featuresQuery.data) return [];
    const features = featuresQuery.data as FeatureData[];
    const result: AccessibleFeature[] = [];

    for (const feature of features) {
      if (feature.sub_features) {
        for (const sub of feature.sub_features) {
          const hidden = hiddenFeatureCodes.includes(sub.code);
          result.push({
            code: sub.code,
            name: sub.name,
            featureCode: feature.feature_code,
            isEnabled: sub.is_enabled === 1 && !hidden,
            isDisabled: sub.is_enabled !== 1 || hidden,
          });
        }
      }
    }

    return result;
  }, [featuresQuery.data, hiddenFeatureCodes]);

  const enabledFeatureCodes = useMemo((): string[] => {
    return accessibleFeatures.filter((f) => f.isEnabled).map((f) => f.code);
  }, [accessibleFeatures]);

  const profile = useMemo((): RBAProfile | null => {
    if (!isReady) return null;
    return {
      role: role as string,
      isSuperAdmin,
      accessibleFeatures,
      accessibleRoles,
      enabledFeatureCodes,
    };
  }, [
    isReady,
    role,
    isSuperAdmin,
    accessibleFeatures,
    accessibleRoles,
    enabledFeatureCodes,
  ]);

  return {
    isLoading,
    isReady,
    isAuthenticated,
    profile,
    role,
    isSuperAdmin,
    isAllowed,
    isDenied,
    allowedRoles,
    checkAccess,
    hasRole,
    hasAnyRole,
    isFeatureEnabled,
    isFeatureDisabled,
    accessibleRoles,
    accessibleFeatures,
    enabledFeatureCodes,
  };
};
